import { useState } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';

import { CompareEvidenceDialog } from './compare-evidence-dialog';
import { formatContent, REF_PLACEHOLDER_TAG } from './kim-message';
import { Reference } from './reference';
import { KIM_MARKED_OPTIONS } from '../../utils/kim-markdown';
import { InconsistencyBlock as InconsistencyBlockType, ReferenceType } from '@/../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

interface KimInconsistencyBlockProps {
  block: InconsistencyBlockType;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}

const CITABLE_REF_TYPES = new Set<ReferenceType>([
  ReferenceType.Fact,
  ReferenceType.Chunk,
  ReferenceType.Document,
  ReferenceType.ThreadFileChunk,
]);

export function KimInconsistencyBlock({ block, referenceIndexMap, onReferenceClick }: KimInconsistencyBlockProps) {
  const references = block.references ?? [];
  const description = renderDescription(block.description ?? [], referenceIndexMap, onReferenceClick);
  const [compareOpen, setCompareOpen] = useState(false);

  return (
    <div className="mx-auto my-4 max-w-3xl rounded-xl border border-red-400/80 bg-white p-3.5 px-3 font-sans">
      <div className="mb-1.75 flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1.5 pl-2 font-sans text-[11px] font-medium tracking-wide text-red-600 uppercase">
          <Icon className="lucide--shield-alert" />
          <span className="mt-px">Inconsistency</span>
        </div>
        {references.length > 0 && (
          <Button onClick={() => setCompareOpen(true)} size="sm" className="-mt-px py-0.75">
            <Icon className="lucide--flip-horizontal-2 text-[11px]" />
            Compare evidence
          </Button>
        )}
      </div>

      <div className="prose prose-sm prose-p:my-1.5 prose-p:leading-normal prose-p:text-gray-800 max-w-none px-2.5">
        {description}
      </div>

      <CompareEvidenceDialog open={compareOpen} onOpenChange={setCompareOpen} references={references} />
    </div>
  );
}

function renderDescription(
  blocks: InconsistencyBlockType['description'],
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>,
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void,
) {
  // formatContent already handles text + reference inline; artifact/survey/inconsistency
  // nested inside a description aren't expected, so they collapse to empty strings.
  const combined = formatContent(blocks);
  const rawHtml = marked.parse(combined, KIM_MARKED_OPTIONS) as string;
  const html = rawHtml.replace(new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'), '$1$2');

  return parse(html, {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
        const id = domNode.attribs['data-id'];
        const refType = domNode.attribs['data-ref-type'] as ReferenceType;
        return (
          <Reference
            index={referenceIndexMap.get(id)?.index ?? 0}
            id={id}
            referenceType={refType}
            onClick={CITABLE_REF_TYPES.has(refType) ? onReferenceClick : undefined}
          />
        );
      }
    },
  });
}
